import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentBookingService } from '../services/appointmentBooking.service';
import { AppointmentBookingRequest, AppointmentBookingResponse } from '../types/appointmentBooking';

export interface TimeSlot {
  startTime: string;
  endTime: string;
  territoryId: string;
  resourceId: string;
  resourceName: string;
  remainingAppointments: number;
  attendeeLimit: number | null;
  workTypeGroupId?: string; // Added field for the 0VSD ID from available-slots API
}

// Removed CampaignMember interface as it's no longer needed

export const useAppointmentBooking = () => {
  const queryClient = useQueryClient();

  const { data: events } = useQuery({
    queryKey: ['events'],
    queryFn: () => appointmentBookingService.getEvents()
  });

  const mutation = useMutation<AppointmentBookingResponse, Error, AppointmentBookingRequest>({
    mutationFn: (bookingData) => appointmentBookingService.bookAppointment(bookingData),
    onSuccess: () => {
      // Invalidate all appointmentSlots queries to refetch fresh availability data
      // This ensures that after booking, the available slots are updated without page reload
      queryClient.invalidateQueries({ queryKey: ['appointmentSlots'] });
    },
    onError: () => {
    }
  });

  const getAvailableSlots = (appointmentId: string, location?: string) => {
    return useQuery({
      queryKey: ['appointmentSlots', appointmentId, location],
      queryFn: async () => {
        const response = await appointmentBookingService.getAvailableSlots(appointmentId, location);
        // Handle both formats: direct array or wrapped in data property
        return Array.isArray(response) ? { data: response } : response;
      },
      enabled: !!appointmentId && (location === undefined || !!location)
    });
  };

  // Always return false for isAppointmentBooked since we're removing booking tracking
  const isAppointmentBooked = (_appointmentId: string): boolean => {
    return false;
  };

  return {
    bookAppointment: mutation.mutate,
    bookAppointmentAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: () => {
      mutation.reset();
    },
    getAvailableSlots,
    isAppointmentBooked, // Always returns false now
    registeredEvents: [], // Empty array since we're not tracking registrations
    events: events || []
  };
};
